import { Phone, Mail, Check, Users } from "lucide-react";

const plans = [
  {
    id: "standard",
    badge: "Standard",
    price: "$50",
    cadence: "/ month",
    title: "Unlimited 24/7 Gym Access",
    blurb: "Full access to every barbell, rack, platform, and machine — any hour of the day.",
    includes: [
      "24/7 access to the entire gym",
      "All barbells, racks & platforms",
      "All training styles welcome",
      "No contracts. No hidden fees.",
    ],
    highlight: true,
  },
  {
    id: "student-military",
    badge: "Student / Military",
    price: "$40",
    cadence: "/ month",
    title: "Unlimited 24/7 Gym Access",
    blurb: "Same full access — discounted for students and active or veteran service members.",
    includes: [
      "24/7 access to the entire gym",
      "Verified student or military ID",
      "All training styles welcome",
      "No contracts. Cancel anytime.",
    ],
  },
  {
    id: "cardio",
    badge: "Cardio Only",
    price: "$25",
    cadence: "/ month",
    title: "Cardio Room Access",
    blurb: "For members focused purely on cardio. Access to the dedicated cardio room.",
    includes: [
      "24/7 access to cardio room",
      "Treadmills, bikes & ellipticals",
      "Quiet, focused space",
      "No contracts. Cancel anytime.",
    ],
  },
];

function PlanCard({ plan }) {
  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`beam-border p-[1.5px] ${plan.highlight ? "lg:scale-[1.02]" : ""}`}
    >
      <div className="relative bg-[#0F0F11] p-6 sm:p-8 h-full flex flex-col">
        {/* Badge */}
        <div className="absolute top-0 left-0 bg-[#0055FF] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
          {plan.badge}
        </div>

        {/* Price */}
        <div className="pt-8 flex items-end gap-2">
          <span className="font-heading font-black text-[4rem] sm:text-[5rem] leading-[0.85] text-white">
            {plan.price}
          </span>
          <div className="pb-2">
            <div className="font-heading font-bold text-base text-white/90">
              {plan.cadence}
            </div>
          </div>
        </div>

        <h3 className="mt-3 font-heading font-extrabold text-lg sm:text-xl uppercase tracking-tight text-white leading-tight">
          {plan.title}
        </h3>
        <p className="mt-3 text-white/65 text-sm font-medium leading-relaxed">
          {plan.blurb}
        </p>

        <div className="h-px w-full bg-white/10 my-6" />

        <ul className="space-y-3 flex-1">
          {plan.includes.map((f, i) => (
            <li
              key={i}
              data-testid={`pricing-${plan.id}-feature-${i}`}
              className="flex items-start gap-3 text-sm text-white/80 font-medium leading-relaxed"
            >
              <span className="mt-0.5 flex-shrink-0 w-4 h-4 bg-[#0055FF] flex items-center justify-center">
                <Check size={11} strokeWidth={3} className="text-white" />
              </span>
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-col gap-3">
          <a
            href="tel:6185500625"
            data-testid={`pricing-${plan.id}-call-btn`}
            className="btn-primary px-5 py-3 font-heading font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2"
          >
            <Phone size={15} />
            Call To Join
          </a>
          <a
            href="mailto:jfbarbell@gmail.com?subject=Membership%20Setup"
            data-testid={`pricing-${plan.id}-email-btn`}
            className="btn-secondary px-5 py-3 font-heading font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2"
          >
            <Mail size={15} />
            Email To Join
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 lg:py-32 px-6 lg:px-10 bg-[#050505] overflow-hidden"
    >
      {/* Ambient blue glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[#0055FF]/10 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Label */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#0055FF]" />
          <span
            className="text-xs font-bold uppercase tracking-[0.3em] text-[#0055FF]"
            data-testid="pricing-eyebrow"
          >
            Membership
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-14">
          <h2
            data-testid="pricing-heading"
            className="lg:col-span-8 font-heading font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
          >
            Pick Your Plan.
            <br />
            <span className="text-[#0055FF]">Join The Crew.</span>
          </h2>
          <p
            data-testid="pricing-subheading"
            className="lg:col-span-4 text-white/65 text-base sm:text-lg font-medium leading-relaxed"
          >
            Honest pricing. No contracts. Memberships are set up by phone or
            email — no online signup.
          </p>
        </div>

        {/* Plan cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        {/* Family Plan add-on banner */}
        <div
          data-testid="pricing-family-card"
          className="mt-6 beam-border p-[1.5px]"
        >
          <div className="relative bg-[#0F0F11] p-8 sm:p-10 lg:p-12 overflow-hidden">
            <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[#0055FF]/25 blur-[120px] pointer-events-none" />
            <div className="absolute top-0 left-0 bg-[#0055FF] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
              Add-On · Family Plans
            </div>

            <div className="relative pt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3">
                  <Users className="text-[#0055FF]" size={28} />
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">
                    Bring The Whole Family
                  </span>
                </div>
                <h3 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-white leading-[0.95]">
                  Family Plans Are
                  <br />
                  <span className="text-[#0055FF]">+$20 per Additional Member</span>
                </h3>
                <p className="mt-4 text-white/70 text-base font-medium leading-relaxed max-w-xl">
                  Add to any base plan above. Each additional family member is
                  just <span className="text-white font-bold">$20 / month</span>.
                  Train together, save together.
                </p>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-3">
                <a
                  href="tel:6185500625"
                  data-testid="pricing-family-call-btn"
                  className="btn-primary px-6 py-4 font-heading font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-3"
                >
                  <Phone size={18} />
                  Call (618) 550-0625
                </a>
                <a
                  href="mailto:jfbarbell@gmail.com?subject=Family%20Plan%20Inquiry"
                  data-testid="pricing-family-email-btn"
                  className="btn-secondary px-6 py-4 font-heading font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-3"
                >
                  <Mail size={18} />
                  Email For Family Plan
                </a>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-xs text-white/40 uppercase tracking-[0.2em] text-center">
          Memberships are set up by phone or email — no online signup.
        </p>
      </div>
    </section>
  );
}
